import React from 'react';
import { Alert, Card, Descriptions, Tag, Typography } from 'antd';
import type { SupportCard } from './types';

const { Paragraph, Text } = Typography;

const formatBytes = (value: unknown) => {
  const bytes = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

const SourceCard: React.FC<{ sources: any[] }> = ({ sources }) => {
  if (!sources?.length) return null;
  return (
    <Card size="small" className="customer-service-card" title="参考文档 / References">
      <div className="customer-service-tags">
        {sources.map((source) => (
          <Tag key={source.id || source.title} color="blue">{source.title}</Tag>
        ))}
      </div>
    </Card>
  );
};

const UploadIssueCard: React.FC<{ issue?: any; steps?: string[] }> = ({ issue = {}, steps = [] }) => (
  <Card size="small" className="customer-service-card" title="上传排查 / Upload Check">
    <Descriptions size="small" column={1} bordered>
      <Descriptions.Item label="文件名">{issue.fileName || '-'}</Descriptions.Item>
      <Descriptions.Item label="错误">{issue.errorMessage || '-'}</Descriptions.Item>
      <Descriptions.Item label="Trace ID">{issue.traceId ? <Text copyable code>{issue.traceId}</Text> : '-'}</Descriptions.Item>
    </Descriptions>
    {steps.length > 0 && (
      <ol className="customer-service-steps">
        {steps.map((step) => <li key={step}>{step}</li>)}
      </ol>
    )}
  </Card>
);

const GuideCard: React.FC<{ title?: string; items?: string[] }> = ({ title, items = [] }) => (
  <Alert
    className="customer-service-card"
    type="info"
    showIcon
    message={title || '操作建议'}
    description={<ul className="customer-service-steps">{items.map((item) => <li key={item}>{item}</li>)}</ul>}
  />
);

const UsageCard: React.FC<{ user?: any; storage?: any }> = ({ user, storage }) => {
  if (!user && !storage) return null;
  return (
    <Card size="small" className="customer-service-card" title="账号与用量 / Account & Usage">
      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label="UID">{user?.emailMasked || user?.uid || '-'}</Descriptions.Item>
        <Descriptions.Item label="登录">{user?.loggedIn ? '已登录' : '游客/未知'}</Descriptions.Item>
        <Descriptions.Item label="VIP">{user?.vip ? '已开通' : '未开通/未知'}</Descriptions.Item>
        {user?.vipEndTime && <Descriptions.Item label="到期">{user.vipEndTime}</Descriptions.Item>}
        {storage?.totalSize !== undefined && <Descriptions.Item label="容量">{formatBytes(storage.totalSize)}</Descriptions.Item>}
        {storage?.fileCount !== undefined && <Descriptions.Item label="文件数">{storage.fileCount}</Descriptions.Item>}
      </Descriptions>
    </Card>
  );
};

const TicketCard: React.FC<{ summary?: string }> = ({ summary }) => {
  if (!summary) return null;
  return (
    <Card size="small" className="customer-service-card" title="人工客服摘要 / Support Summary">
      <Paragraph copyable className="customer-service-ticket">{summary}</Paragraph>
    </Card>
  );
};

export const renderSupportCard = (card: SupportCard, index: number) => {
  switch (card.type) {
    case 'support_sources':
      return <SourceCard key={index} sources={card.data?.sources || []} />;
    case 'upload_issue':
      return <UploadIssueCard key={index} issue={card.data?.issue} steps={card.data?.steps} />;
    case 'guide':
      return <GuideCard key={index} title={card.data?.title} items={card.data?.items || []} />;
    case 'usage_summary':
      return <UsageCard key={index} user={card.data?.user} storage={card.data?.storage} />;
    case 'support_ticket':
      return <TicketCard key={index} summary={card.data?.summary} />;
    default:
      return null;
  }
};
